/**
 * LOGOS v5.0 — Emergence Theory Visualizations
 * Sub-components: SNR Gauge, Multi-Scale Lambda, Criticality Alert, Coherence Matrix, Emergence Index
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  computeMultiScaleLambda,
  computeShannonSNR,
  computeCoherenceMatrix,
  DOMAIN_DEFINITIONS,
} from '../core/emergence-metrics';

const COLORS = {
  bg: '#0F0F1A', card: '#1A1A2E', border: '#2D2D44',
  text: '#E0E0F0', textDim: '#8888AA', logos: '#C084FC',
};

// ─── Shannon SNR Gauge ─── [P2]
export function SNRGauge({ lambda, entropy, hoffmanBiases = null }) {
  const { t } = useTranslation();
  const snr = useMemo(
    () => computeShannonSNR(lambda, entropy, hoffmanBiases),
    [lambda, entropy, hoffmanBiases]
  );

  return (
    <div style={{
      background: COLORS.card, borderRadius: '12px', padding: '14px',
      border: `1px solid ${COLORS.border}`, textAlign: 'center',
    }}>
      <div style={{ color: COLORS.textDim, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {t('header.channelQuality', 'SNR')}
      </div>

      {/* Gauge bar */}
      <div style={{
        height: '8px', borderRadius: '4px', background: '#1A1A2E',
        margin: '10px 0', overflow: 'hidden', border: `1px solid ${COLORS.border}`,
      }}>
        <div style={{
          height: '100%', width: `${snr.gaugePercent}%`, borderRadius: '4px',
          background: `linear-gradient(90deg, ${snr.color}40, ${snr.color})`,
          transition: 'width 0.5s ease',
        }} />
      </div>

      <div style={{ color: snr.color, fontSize: '18px', fontWeight: 700 }}>
        {snr.snr.toFixed(2)} dB
      </div>
      <div style={{ color: snr.color, fontSize: '12px', marginTop: '2px' }}>
        {snr.quality}
      </div>
      <div style={{ color: COLORS.textDim, fontSize: '10px', marginTop: '4px' }}>
        {t('misc.signalNoise', { lambda: lambda.toFixed(2), entropy: entropy.toFixed(2) })}
      </div>
    </div>
  );
}

// ─── Multi-Scale Lambda ─── [P1]
export function MultiScaleLambdaCard({ state }) {
  const { t } = useTranslation();
  const result = useMemo(
    () => computeMultiScaleLambda(state),
    [state]
  );

  const domains = Object.entries(DOMAIN_DEFINITIONS);

  return (
    <div style={{
      background: COLORS.card, borderRadius: '12px', padding: '14px',
      border: `1px solid ${COLORS.border}`,
    }}>
      <div style={{
        color: COLORS.textDim, fontSize: '11px', textTransform: 'uppercase',
        letterSpacing: '0.5px', marginBottom: '10px', textAlign: 'center',
      }}>
        Λ Multi-Scale
      </div>

      {domains.map(([key, dom]) => {
        const val = result.byDomain[key] || 0;
        const isStrongest = key === result.strongest;
        const isWeakest = key === result.weakest;

        return (
          <div key={key} style={{ marginBottom: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '2px' }}>
              <span style={{ color: dom.color }}>
                {dom.label}
                {isStrongest && ' 🔥'}
                {isWeakest && ' ⚠️'}
              </span>
              <span style={{ color: COLORS.text, fontWeight: 600 }}>
                Λ_{key.slice(0, 3)} = {val.toFixed(3)}
              </span>
            </div>
            <div style={{
              height: '6px', borderRadius: '3px', background: '#0F0F1A',
              overflow: 'hidden',
            }}>
              <div style={{
                height: '100%', width: `${val * 100}%`, borderRadius: '3px',
                background: `linear-gradient(90deg, ${dom.color}60, ${dom.color})`,
                transition: 'width 0.4s ease',
              }} />
            </div>
          </div>
        );
      })}

      <div style={{
        marginTop: '10px', padding: '8px', borderRadius: '8px',
        background: `${COLORS.logos}10`, border: `1px solid ${COLORS.logos}30`,
        fontSize: '11px', color: COLORS.textDim, textAlign: 'center',
      }}>
        Spread: {result.spread.toFixed(3)} — {result.spread < 0.15 ? t('misc.spreadHigh') : result.spread < 0.30 ? t('misc.spreadMod') : t('misc.spreadLow')}
      </div>
    </div>
  );
}

// ─── Criticality Alert Banner ─── [P1]
export function CriticalityAlertBanner({ alert }) {
  const { t } = useTranslation();
  if (!alert || !alert.active) return null;

  const severityStyles = {
    watch: { bg: '#3B82F620', border: '#3B82F640', color: '#60A5FA', icon: '👀' },
    warning: { bg: '#F59E0B15', border: '#F59E0B40', color: '#FBBF24', icon: '⚡' },
    critical: { bg: '#EF444420', border: '#EF444450', color: '#F87171', icon: '🚨' },
  };

  const style = severityStyles[alert.severity] || severityStyles.watch;

  return (
    <div style={{
      background: style.bg, border: `1px solid ${style.border}`,
      borderRadius: '12px', padding: '12px 16px', marginBottom: '12px',
      animation: alert.severity === 'critical' ? 'pulse 2s infinite' : 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: '20px' }}>{style.icon}</span>
        <div>
          <div style={{ color: style.color, fontSize: '13px', fontWeight: 600 }}>
            {alert.severity === 'critical' ? t('alerts.critical') :
             alert.severity === 'warning' ? t('alerts.warning') : t('alerts.observation')}
          </div>
          <div style={{ color: '#E0E0F0', fontSize: '12px', marginTop: '2px' }}>
            {alert.message}
          </div>
          {alert.timeToCollapse && (
            <div style={{ color: style.color, fontSize: '11px', marginTop: '4px', fontWeight: 600 }}>
              {t('alerts.estimatedTime', { days: alert.timeToCollapse })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Coherence Matrix Heatmap ─── [P3]
export function CoherenceMatrixHeatmap({ state }) {
  const result = useMemo(
    () => computeCoherenceMatrix(state),
    [state]
  );

  const n = result.domainLabels.length;
  const cellSize = 38;
  const labelWidth = 60;

  const getColor = (val) => {
    if (val >= 0.5) return `rgba(16,185,129,${0.2 + val * 0.6})`;
    if (val >= 0) return `rgba(245,158,11,${0.1 + val * 0.4})`;
    return `rgba(239,68,68,${0.1 + Math.abs(val) * 0.5})`;
  };

  return (
    <div style={{
      background: COLORS.card, borderRadius: '12px', padding: '14px',
      border: `1px solid ${COLORS.border}`, overflowX: 'auto',
    }}>
      <div style={{
        color: COLORS.textDim, fontSize: '11px', textTransform: 'uppercase',
        letterSpacing: '0.5px', marginBottom: '10px', textAlign: 'center',
      }}>
        Matriz de Coherencia Inter-Dominio
      </div>

      <div style={{ display: 'flex' }}>
        {/* Row labels */}
        <div style={{ marginRight: '4px', paddingTop: `${cellSize + 6}px` }}>
          {result.domainLabels.map((label, i) => (
            <div key={i} style={{
              height: cellSize + 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
              fontSize: '9px', color: DOMAIN_DEFINITIONS[Object.keys(DOMAIN_DEFINITIONS)[i]]?.color || COLORS.textDim,
              width: labelWidth, paddingRight: '4px',
            }}>
              {label.slice(0, 5)}
            </div>
          ))}
        </div>

        {/* Matrix grid */}
        <div>
          {/* Column labels */}
          <div style={{ display: 'flex', marginBottom: '4px' }}>
            {result.domainLabels.map((label, j) => (
              <div key={j} style={{
                width: cellSize + 2, textAlign: 'center', fontSize: '8px',
                color: DOMAIN_DEFINITIONS[Object.keys(DOMAIN_DEFINITIONS)[j]]?.color || COLORS.textDim,
                transform: 'rotate(-35deg)', transformOrigin: 'center bottom',
                height: cellSize,
              }}>
                {label.slice(0, 4)}
              </div>
            ))}
          </div>

          {/* Cells */}
          {result.matrix.map((row, i) => (
            <div key={i} style={{ display: 'flex' }}>
              {row.map((val, j) => (
                <div key={j} style={{
                  width: cellSize, height: cellSize, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  background: getColor(val), borderRadius: '4px',
                  margin: '1px', fontSize: '9px', fontWeight: 600,
                  color: i === j ? COLORS.textDim : COLORS.text,
                }}>
                  {i === j ? '—' : val.toFixed(2)}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Insights */}
      <div style={{
        marginTop: '10px', fontSize: '11px', color: COLORS.textDim, textAlign: 'center',
      }}>
        Coupling más fuerte: <span style={{ color: '#10B981' }}>
          {result.strongestCoupling[0]}↔{result.strongestCoupling[1]} (ρ={result.strongestCoupling[2].toFixed(2)})
        </span>
      </div>
    </div>
  );
}

// ─── Emergence Index Card ─── [P2]
export function EmergenceIndexCard({ emergenceIndex }) {
  if (!emergenceIndex) return null;

  const { value, interpretation } = emergenceIndex;
  const color = value > 0.65 ? '#10B981' : value > 0.40 ? '#F59E0B' : '#EF4444';

  return (
    <div style={{
      background: `${color}10`, borderRadius: '12px', padding: '12px',
      border: `1px solid ${color}30`, textAlign: 'center',
    }}>
      <div style={{ color: COLORS.textDim, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        E(x) Emergence Index
      </div>
      <div style={{ color, fontSize: '26px', fontWeight: 700, marginTop: '4px' }}>
        {value.toFixed(3)}
      </div>
      <div style={{ color, fontSize: '12px', marginTop: '2px' }}>
        {interpretation}
      </div>
      <div style={{ color: COLORS.textDim, fontSize: '10px', marginTop: '4px' }}>
        σ² = {emergenceIndex.variance.toFixed(5)} | Coherencia dinámica entre dominios
      </div>
    </div>
  );
}
